import React from 'react';
import { UserPlus, Mail, Calendar, Hash } from 'lucide-react';
import { AffiliateUser } from '../../../services/adminUserService.ts';
import { ClickableUserName } from '../../common/ClickableUserName';

interface AdminUserAffiliatesSectionProps {
  affiliates: AffiliateUser[];
}

export function AdminUserAffiliatesSection({ affiliates }: AdminUserAffiliatesSectionProps) {

  if (!affiliates.length) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        <UserPlus className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-medium text-gray-600 mb-2">No affiliates registered</p>
        <p className="text-sm text-gray-500">This user has not referred any other users yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Registered Affiliates</h2>
            <p className="text-sm text-gray-500 mt-1">Users who registered using this user's affiliate code</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-wayja-teal/10 rounded-lg">
            <UserPlus className="w-4 h-4 text-wayja-teal" />
            <span className="text-sm font-semibold text-wayja-teal">{affiliates.length}</span>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <HeaderCell>User</HeaderCell>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Affiliate Code</HeaderCell>
              <HeaderCell>Registered</HeaderCell>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {affiliates.map((affiliate) => (
              <tr key={affiliate.id} className="hover:bg-gray-50 transition-colors">
                <BodyCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-wayja-teal/10 flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-wayja-teal" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        <ClickableUserName
                          userId={affiliate.id}
                          displayName={affiliate.full_name || affiliate.username || affiliate.email.split('@')[0]}
                        />
                      </div>
                      {affiliate.full_name && (
                        <div className="text-xs text-gray-500">{affiliate.username || affiliate.email.split('@')[0]}</div>
                      )}
                    </div>
                  </div>
                </BodyCell>
                <BodyCell>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{affiliate.email}</span>
                  </div>
                </BodyCell>
                <BodyCell>
                  {affiliate.affiliate_code ? (
                    <div className="flex items-center gap-2">
                      <Hash className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        {affiliate.affiliate_code}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </BodyCell>
                <BodyCell>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{new Date(affiliate.created_at).toLocaleDateString()}</span>
                  </div>
                </BodyCell>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface HeaderCellProps {
  children: React.ReactNode;
  className?: string;
}

function HeaderCell({ children, className }: HeaderCellProps) {
  return (
    <th
      scope="col"
      className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 ${className || ''}`}
    >
      {children}
    </th>
  );
}

interface BodyCellProps {
  children: React.ReactNode;
  className?: string;
}

function BodyCell({ children, className }: BodyCellProps) {
  return <td className={`px-6 py-4 whitespace-nowrap text-sm ${className || ''}`}>{children}</td>;
}

